import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Eye, EyeOff, CheckCircle, XCircle,
  Shield, Loader2, Anchor, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'digitalocean_creds';

const encrypt = (creds) => btoa(JSON.stringify(creds));
const decrypt = (s) => { try { return JSON.parse(atob(s)); } catch { return null; } };

const DigitalOceanConnect = ({ isOpen, onClose, onConnected }) => {
  const [credentials, setCredentials] = useState({ access_token: '', account_label: '' });
  const [showToken, setShowToken] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const d = decrypt(saved);
      if (d) { setCredentials(d); setRemember(true); }
    }
  }, []);

  const handleConnect = async () => {
    if (!credentials.access_token) {
      toast.error('Personal Access Token is required');
      return;
    }
    setIsConnecting(true);
    setConnectionStatus(null);
    try {
      const response = await axios.post(`${API_URL}/api/cloud-audit/digitalocean`, credentials);
      if (response.data?.provider === 'DigitalOcean') {
        setConnectionStatus({
          success: true,
          account_id: response.data.account_id,
          resources_scanned: response.data.resources_scanned,
          note: response.data.note,
        });
        if (remember) localStorage.setItem(STORAGE_KEY, encrypt(credentials));
        else localStorage.removeItem(STORAGE_KEY);
        sessionStorage.setItem('digitalocean_session', encrypt(credentials));
        toast.success('Connected to DigitalOcean!');
        onConnected?.(credentials, response.data);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Connection failed';
      setConnectionStatus({ success: false, error: message });
      toast.error(message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleForget = () => {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem('digitalocean_session');
    setCredentials({ access_token: '', account_label: '' });
    setRemember(false);
    setConnectionStatus(null);
    toast.info('Credentials cleared from this browser');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="do-connect-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#0080FF' }}>
              <Anchor className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle>Connect DigitalOcean</DialogTitle>
              <DialogDescription>Read-only Personal Access Token (PAT)</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-3 rounded-md text-sm text-blue-900 dark:text-blue-200">
            <div className="font-medium mb-1 flex items-center gap-1.5"><Shield className="w-4 h-4" />How to create your token</div>
            <ol className="list-decimal pl-5 space-y-0.5 text-xs">
              <li>Open <a className="underline" href="https://cloud.digitalocean.com/account/api/tokens" target="_blank" rel="noreferrer">cloud.digitalocean.com/account/api/tokens</a></li>
              <li>Click <strong>Generate New Token</strong></li>
              <li>Name it <code className="text-[10px]">cartie-ai-readonly</code>, scope: <strong>Read</strong> only</li>
              <li>Copy the token (shown once) and paste below</li>
            </ol>
          </div>

          <div>
            <Label htmlFor="do-label">Account label (optional)</Label>
            <Input id="do-label" data-testid="do-label-input"
              placeholder="Production · Team A"
              value={credentials.account_label}
              onChange={(e) => setCredentials({ ...credentials, account_label: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="do-token">Personal Access Token</Label>
            <div className="relative">
              <Input id="do-token" data-testid="do-token-input"
                type={showToken ? 'text' : 'password'}
                placeholder="dop_v1_..."
                value={credentials.access_token}
                onChange={(e) => setCredentials({ ...credentials, access_token: e.target.value })}
                className="pr-10"
              />
              <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500">
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            Remember on this browser (encrypted in localStorage)
          </label>

          {connectionStatus && (
            <div className={`p-3 rounded-md flex items-start gap-2 text-sm ${connectionStatus.success
                ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800'
                : 'bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800'}`}>
              {connectionStatus.success
                ? <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
                : <XCircle className="w-4 h-4 text-red-600 mt-0.5" />}
              <div>
                {connectionStatus.success ? (
                  <>
                    <div className="font-medium text-emerald-700 dark:text-emerald-300">Connected</div>
                    <div className="text-xs text-emerald-700/80 dark:text-emerald-300/80">{connectionStatus.note}</div>
                  </>
                ) : (
                  <div className="text-red-700 dark:text-red-300">{connectionStatus.error}</div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          {localStorage.getItem(STORAGE_KEY) && (
            <Button variant="ghost" size="sm" onClick={handleForget} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-1" /> Forget
            </Button>
          )}
          <Button variant="outline" onClick={onClose} data-testid="do-cancel">Cancel</Button>
          <Button onClick={handleConnect} disabled={isConnecting} data-testid="do-connect-submit"
            style={{ backgroundColor: '#0080FF', color: 'white' }}>
            {isConnecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connecting...</> : 'Connect & Audit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const useDigitalOceanCredentials = () => {
  const [credentials, setCredentials] = useState(null);

  useEffect(() => {
    const s = sessionStorage.getItem('digitalocean_session') || localStorage.getItem(STORAGE_KEY);
    if (s) setCredentials(decrypt(s));
  }, []);

  const updateCredentials = (creds, persist = false) => {
    setCredentials(creds);
    sessionStorage.setItem('digitalocean_session', encrypt(creds));
    if (persist) localStorage.setItem(STORAGE_KEY, encrypt(creds));
  };

  const disconnect = () => {
    setCredentials(null);
    sessionStorage.removeItem('digitalocean_session');
    localStorage.removeItem(STORAGE_KEY);
  };

  return { credentials, isConnected: !!credentials, updateCredentials, disconnect };
};

export default DigitalOceanConnect;
